import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import InterestRateCard from '../components/InterestRateCard';
import InterestRateModal from '../components/InterestRateModal';
import ConfirmDialog from '../components/ConfirmDialog';

const BalanceTransferLoanInterestRatesPage = () => {
    const [cardsList, setCardsList] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Affordable Home Loan',
        section_subtitle: 'Interest Rates',
        intro_text: 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home construction affordable and stress-free.',
        closing_text: 'Begin your home construction journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs.',
        cta_text: 'Calculate EMI',
        cta_link: 'https://aham.mbwhost.in/emi-calculator'
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, cardId: null });

    const LOAN_TYPE = 'balance_transfer_loan';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [cardsRes, settingsRes] = await Promise.all([
                axios.get(`/api/admin/interest-rates/${LOAN_TYPE}`),
                axios.get(`/api/admin/interest-rates/settings/${LOAN_TYPE}`)
            ]);

            setCardsList(cardsRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load interest rates data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingCard) {
                await axios.put(`/api/admin/interest-rates/${editingCard.id}`, formData);
                toast.success('Card updated successfully');
            } else {
                await axios.post('/api/admin/interest-rates', formData);
                toast.success('Card created successfully');
            }
            setIsModalOpen(false);
            setEditingCard(null);
            fetchData();
        } catch (error) {
            console.error('Error saving card:', error);
            toast.error('Failed to save card');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, cardId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/interest-rates/${deleteConfirm.cardId}`);
            toast.success('Card deleted successfully');
            setDeleteConfirm({ isOpen: false, cardId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting card:', error);
            toast.error('Failed to delete card');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/interest-rates/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Interest Rates</h1>
                    <p className="text-slate-500 mt-1">Manage the interest rates section for Balance Transfer Loan</p>
                </div>
                <button
                    onClick={() => {
                        setEditingCard(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Card
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-primary-600" />
                    Section Settings
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Affordable Home Loan"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Interest Rates"
                        />
                    </div>
                </div>
                <div className="space-y-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Intro Text
                        </label>
                        <textarea
                            rows="2"
                            value={settings.intro_text}
                            onChange={(e) => setSettings({ ...settings, intro_text: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Introduction paragraph..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Closing Text
                        </label>
                        <textarea
                            rows="2"
                            value={settings.closing_text}
                            onChange={(e) => setSettings({ ...settings, closing_text: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Closing paragraph..."
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            CTA Button Text
                        </label>
                        <input
                            type="text"
                            value={settings.cta_text}
                            onChange={(e) => setSettings({ ...settings, cta_text: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Calculate EMI"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            CTA Button Link
                        </label>
                        <input
                            type="text"
                            value={settings.cta_link}
                            onChange={(e) => setSettings({ ...settings, cta_link: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., /emi-calculator"
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Cards Grid */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-6">Feature Cards</h2>
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl h-40 shadow-sm animate-pulse border border-slate-100"></div>
                        ))}
                    </div>
                ) : cardsList.length === 0 ? (
                    <div className="bg-slate-50 rounded-2xl p-12 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <SparklesIcon className="h-8 w-8 text-slate-400" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2">No cards yet</h3>
                            <p className="text-slate-500 mb-6">Add your first interest rate feature card.</p>
                            <button
                                onClick={() => {
                                    setEditingCard(null);
                                    setIsModalOpen(true);
                                }}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                            >
                                Add First Card
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {cardsList.map((card) => (
                            <InterestRateCard
                                key={card.id}
                                card={card}
                                onEdit={(c) => {
                                    setEditingCard(c);
                                    setIsModalOpen(true);
                                }}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            <InterestRateModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingCard(null);
                }}
                onSubmit={handleSubmit}
                card={editingCard}
                loading={submitting}
                loanType={LOAN_TYPE}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, cardId: null })}
                onConfirm={confirmDelete}
                title="Delete Card"
                message="Are you sure you want to delete this card? This action cannot be undone."
            />
        </div>
    );
};

export default BalanceTransferLoanInterestRatesPage;
